/**
 * Web Push (VAPID) — comunicação com /api/push/ no backend.
 *
 * O navegador devolve `PushSubscription.toJSON()` no formato:
 *   { endpoint, keys: { p256dh, auth } }
 * que é exatamente o que o `PushSubscriptionSerializer` aceita.
 */
import { apiClient } from '~/api/client';
import type { components } from '~/types/api';

export type PushSubscriptionResponse = components['schemas']['PushSubscriptionResponse'];
type VapidPublicKeyResponse = components['schemas']['VapidPublicKey'];

export async function fetchVapidPublicKey(): Promise<string> {
  const response = await apiClient.get<VapidPublicKeyResponse>('/api/push/vapid-public-key/');
  return response.data.public_key;
}

export async function subscribePush(
  subscription: PushSubscriptionJSON,
): Promise<PushSubscriptionResponse> {
  const response = await apiClient.post<PushSubscriptionResponse>('/api/push/subscriptions/', {
    endpoint: subscription.endpoint,
    keys: subscription.keys,
    user_agent: navigator.userAgent,
  });
  return response.data;
}

export async function unsubscribePush(id: string): Promise<void> {
  await apiClient.delete(`/api/push/subscriptions/${id}/`);
}

/**
 * Converte a chave pública VAPID (URL-safe base64) para Uint8Array<ArrayBuffer>,
 * formato exigido pelo `pushManager.subscribe({ applicationServerKey })`.
 * Cria o ArrayBuffer explicitamente pra não cair em SharedArrayBuffer
 * (TS 5.7+ separa os dois e o tipo do DOM exige ArrayBuffer).
 */
export function urlBase64ToUint8Array(base64String: string): Uint8Array<ArrayBuffer> {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const rawData = atob(base64);
  const buffer = new ArrayBuffer(rawData.length);
  const output = new Uint8Array(buffer);
  for (let i = 0; i < rawData.length; i += 1) {
    output[i] = rawData.charCodeAt(i);
  }
  return output;
}
